// src/firebase/invoice/getInvoices.ts
import { ref, get } from "firebase/database";
import { db } from "../firebaseConfig";
import { InvoiceData } from "../../models/invoice";
import { convertUTCToIST } from "../../utils/calculations";
import { ReturnItemData } from "../../models/credit";
import { getAllReturnedItems } from "../returns/getAllReturnedItems";

export interface InvoiceWithReturns extends InvoiceData {
	returns: ReturnItemData[];
}

/**
 * Get Invoices
 * @returns : List of all invoices
 */
export const getInvoices = async (): Promise<{
	[key: string]: InvoiceData;
}> => {
	try {
		const invoiceRef = ref(db, "invoices"); // Flat structure
		const snapshot = await get(invoiceRef);

		if (!snapshot.exists()) {
			return {};
		}

		return snapshot.val();
	} catch (error) {
		console.error("Error fetching invoices:", error);
		throw error;
	}
};

/**
 * Get Invoices by Date Range
 * @param startDate : Start Date in IST
 * @param endDate : End Date in IST
 * @returns : List of invoices if found
 */
export const getInvoicesByDateRange = async (
	startDateIST: string,
	endDateIST: string
): Promise<{ [key: string]: InvoiceData }> => {
	try {
		const invoiceRef = ref(db, "invoices");
		const snapshot = await get(invoiceRef);
		if (!snapshot.exists()) {
			return {};
		}
		const invoices = snapshot.val() as { [key: string]: InvoiceData };
		const filteredInvoices: { [key: string]: InvoiceData } = {};
		Object.entries(invoices).forEach(([key, invoice]) => {
			const invoiceDate = convertUTCToIST(invoice.createdAt).substring(
				0,
				10
			);
			if (invoiceDate >= startDateIST && invoiceDate <= endDateIST) {
				filteredInvoices[key] = invoice;
			}
		});
		return filteredInvoices;
	} catch (error) {
		console.error("Error fetching invoices by date range:", error);
		throw error;
	}
};

/**
 * Get Invoices with their returned items by Date Range
 * @param startDateIST : Start Date in IST
 * @param endDateIST : End Date in IST
 * @returns : List of invoices with returned items if found
 */
export const getInvoicesWithReturnsByDateRange = async (
	startDateIST: string,
	endDateIST: string
): Promise<InvoiceWithReturns[]> => {
	try {
		const [invoices, allReturnedItems] = await Promise.all([
			getInvoicesByDateRange(startDateIST, endDateIST),
			getAllReturnedItems()
		]);
		const invoicesWithReturns: InvoiceWithReturns[] = [];

		for (const key in invoices) {
			if (Object.prototype.hasOwnProperty.call(invoices, key)) {
				const invoice = invoices[key];
				const returnedItems =
					allReturnedItems[invoice.invoiceNumber] || [];
				invoicesWithReturns.push({
					...invoice,
					returns: returnedItems
				});
			}
		}
		return invoicesWithReturns;
	} catch (error) {
		console.error(
			"Error fetching invoices with returns by date range:",
			error
		);
		throw error;
	}
};
